/**
 * WellnessPlanEmailCTA — soft-conversion email capture on the assessment report.
 *
 * Flow:
 *   1. Visitor lands on /report (works for both registered users AND future
 *      guest-mode visitors — they all have an `assessment_id`).
 *   2. Calm bottom-card CTA: "Save your wellness plan".
 *   3. Tap → inline email input + optional "Send me gentle wellness tips" checkbox.
 *   4. Submit → POST /api/wellness-plan/email → server generates 1-page PDF +
 *      emails it via Resend AND returns a `capture_id` + `download_url`.
 *   5. Success state: "✓ Check your inbox" + "Download now" button that
 *      streams the PDF for instant satisfaction (no email round-trip wait).
 *
 * GA4 events fired (via the unified `trackEvent` facade):
 *   - pdf_request_started      — user opens the email capture form
 *   - email_capture_completed  — POST succeeded; includes marketing_optin
 *   - pdf_downloaded           — user clicked Download to fetch the PDF
 *
 * SSR-safe, no extra deps, mobile-friendly, consent-respecting (trackEvent
 * itself is gated on cookie consent).
 */
import { useState } from "react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { FileDown, Mail, Sparkles, CheckCircle2, Loader2 } from "lucide-react";


function isEmailish(v) {
  return typeof v === "string" && /.+@.+\..+/.test(v.trim());
}


function readGaClientId() {
  // GA4 stores client_id inside the _ga cookie:  "GA1.<depth>.<id1>.<id2>".
  // We extract `<id1>.<id2>` so the server can join Stripe webhook conversions
  // back to the right session via Measurement Protocol later.
  try {
    const m = (document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/) || [])[1];
    if (!m) return null;
    const parts = decodeURIComponent(m).split(".");
    if (parts.length >= 4) return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    return null;
  } catch { return null; }
}


export default function WellnessPlanEmailCTA({ assessmentId, suggestedName }) {
  const [stage, setStage] = useState("closed"); // closed | form | success
  const [email, setEmail] = useState("");
  const [name, setName] = useState(suggestedName || "");
  const [optin, setOptin] = useState(true);
  const [busy, setBusy] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  function openForm() {
    setStage("form");
    try {
      trackEvent("pdf_request_started", {
        location: "report_page",
        assessment_id: assessmentId || null,
      });
    } catch { /* swallow */ }
  }

  async function submit(e) {
    e?.preventDefault?.();
    if (!isEmailish(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/wellness-plan/email", {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        assessment_id: assessmentId || null,
        marketing_optin: optin,
        ga_client_id: readGaClientId(),
      });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = `${base}${data.download_url}`;
      setDownloadUrl(fullUrl);
      setStage("success");
      toast.success("Your wellness plan is on the way to your inbox.");
      try {
        trackEvent("email_capture_completed", {
          location: "report_page",
          source: "wellness_plan_pdf",
          marketing_optin: optin,
          assessment_id: assessmentId || null,
          capture_id: data.capture_id,
        });
      } catch { /* swallow */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send your plan. Please try again in a moment.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  function onDownloadClick() {
    try {
      trackEvent("pdf_downloaded", {
        location: "report_page",
        source: "wellness_plan_pdf",
        assessment_id: assessmentId || null,
      });
    } catch { /* swallow */ }
  }

  return (
    <Card
      className="rounded-3xl border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-amber-50/40 shadow-sm"
      data-testid="wellness-plan-cta-card"
    >
      <CardContent className="p-6 sm:p-8">
        {stage === "closed" && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex items-start gap-3 flex-1">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white border border-emerald-100 shrink-0">
                <FileDown className="w-5 h-5 text-emerald-700" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Take it with you</p>
                <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light mt-1 leading-snug">
                  Save your wellness plan as a free 1-page PDF
                </h3>
                <p className="text-sm text-emerald-900/75 mt-2 leading-relaxed">
                  We'll email a calm, printable summary — assessment snapshot, three insights, three small next steps. No account needed.
                </p>
              </div>
            </div>
            <Button
              onClick={openForm}
              className="bg-emerald-700 hover:bg-emerald-800 text-white shrink-0"
              data-testid="wellness-plan-open-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> Email my plan
            </Button>
          </div>
        )}

        {stage === "form" && (
          <form onSubmit={submit} className="space-y-4" data-testid="wellness-plan-form">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Save your wellness plan
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Your first name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-white"
                data-testid="wellness-plan-name-input"
                disabled={busy}
                maxLength={80}
              />
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white"
                data-testid="wellness-plan-email-input"
                disabled={busy}
                required
                autoFocus
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-emerald-900/80 cursor-pointer">
              <input
                type="checkbox"
                checked={optin}
                onChange={(e) => setOptin(e.target.checked)}
                className="mt-0.5 accent-emerald-700"
                data-testid="wellness-plan-optin-checkbox"
                disabled={busy}
              />
              <span>Send me occasional gentle wellness tips (1–2 emails / month, unsubscribe anytime).</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                disabled={busy || !isEmailish(email)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="wellness-plan-submit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Email my plan
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStage("closed")}
                disabled={busy}
                className="text-emerald-800"
                data-testid="wellness-plan-cancel-btn"
              >
                Cancel
              </Button>
              <p className="text-[11px] text-emerald-900/55 ml-auto">We only use your email to send your plan and (optionally) tips.</p>
            </div>
          </form>
        )}

        {stage === "success" && (
          <div className="space-y-4 text-center sm:text-left" data-testid="wellness-plan-success">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              <p className="font-display text-xl text-emerald-950">Your wellness plan is on its way</p>
            </div>
            <p className="text-sm text-emerald-900/75 leading-relaxed">
              Check your inbox at <strong>{email}</strong>. If you don't see it in a minute or two, check your spam folder. You can also grab the PDF right now:
            </p>
            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start">
              {downloadUrl && (
                <Button
                  asChild
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="wellness-plan-download-btn"
                >
                  <a href={downloadUrl} target="_blank" rel="noopener noreferrer" onClick={onDownloadClick}>
                    <FileDown className="w-4 h-4 mr-2" /> Download PDF now
                  </a>
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setStage("closed"); setEmail(""); }}
                className="text-emerald-800"
                data-testid="wellness-plan-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
